import { useEffect, useRef, useState } from "react";
import { Button, Input, message, Popconfirm, Select, Space, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { serverFetch } from "@/server";
import { RefreshButton, ScanButton } from "@/extensions/design";
import { hasSubstring } from "@/extensions/helper";
import { Scan } from "@/controls";
import Card from "./card";
import CardVid from "./card-vid";

const objectVidNames = {
  1: "Клиент",
  3: "Визит",
  8: "Сотрудник",
};

const Cards = () => {
  const [vids, setVids] = useState([]);
  const [vidId, setVidId] = useState();
  const [items, setItems] = useState([]);
  const [company, setCompany] = useState();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [editVidId, setEditVidId] = useState();
  const [scanCode, setScanCode] = useState("");
  const divRef = useRef(null);

  const canEdit = company?.userRole != null && company.userRole <= 10;
  const selectedVid = vids.find(vid => vid.id === vidId);

  const getVids = () => {
    setLoading(true);
    serverFetch("Card/vid")
      .then(data => {
        const loadedVids = Array.isArray(data) ? data : [];
        setVids(loadedVids);
        setVidId(current => {
          if (current === null) return null;
          if (loadedVids.some(vid => vid.id === current)) return current;
          return loadedVids[0]?.id;
        });
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const getCards = () => {
    if (vidId === undefined) {
      setItems([]);
      return;
    }

    setLoading(true);
    serverFetch("Card", { queryParams: vidId == null ? {} : { vidId } })
      .then(data => {
        setItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    getVids();
    serverFetch("company/current")
      .then(setCompany)
      .catch(e => message.error(e.userMessage));
  }, []);

  useEffect(getCards, [vidId]);

  const deleteCard = (card, event) => {
    event?.stopPropagation();
    setLoading(true);
    serverFetch(`Card/${card.id}`, { method: "DELETE" })
      .then(getCards)
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const deleteVid = () => {
    setLoading(true);
    serverFetch(`Card/vid/${vidId}`, { method: "DELETE" })
      .then(() => {
        setVidId(undefined);
        getVids();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const closeCard = refresh => {
    setEditId(undefined);
    setScanCode("");
    if (refresh) getCards();
  };

  const onScan = value => {
    const code = value?.trim();
    if (!code) return;

    setLoading(true);
    serverFetch("Card", { queryParams: { search: code } })
      .then(data => {
        setLoading(false);
        const cards = Array.isArray(data) ? data : [];
        const found = cards.find(card => (card.code ?? card.Code)?.trim().toLowerCase() === code.toLowerCase());
        const cardId = found?.id ?? found?.Id;
        if (cardId) {
          const foundVidId = found?.vidId ?? found?.VidId;
          if (foundVidId != null) setVidId(foundVidId);
          setScanCode("");
          setEditId(cardId);
          return;
        }
        setScanCode(code);
        setEditId(0);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const closeVid = refresh => {
    setEditVidId(undefined);
    if (refresh) getVids();
  };

  const vidOptions = vids.map(vid => ({
    value: vid.id,
    label: <Space size={6}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: vid.colorCode || "#CCCCCC", display: "inline-block" }} />
      <span>{vid.name}</span>
    </Space>,
  }));

  const columns = [
    { title: "#", dataIndex: "id", width: 70 },
    { title: "Код", dataIndex: "code", width: 180 },
    { title: "Наименование", dataIndex: "name", minWidth: 180 },
    {
      title: "Группа",
      dataIndex: "vidId",
      minWidth: 160,
      render: vidIdValue => {
        const vid = vids.find(item => item.id === vidIdValue);
        if (!vid) return "";
        return <Space size={6}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: vid.colorCode || "#CCCCCC", display: "inline-block" }} />
          <span>{vid.name}</span>
        </Space>;
      },
    },
    {
      title: "Статус",
      key: "status",
      width: 170,
      render: (_, card) => card.objectVid == null
        ? <Tag color="green">Свободна</Tag>
        : <Tag>{objectVidNames[card.objectVid] ?? "Объект"} #{card.objectId}</Tag>,
    },
    { title: "Комментарий", dataIndex: "comment", minWidth: 180 },
    ...(canEdit ? [{
      title: "",
      key: "delete",
      width: 48,
      render: (_, card) => <Popconfirm
        title="Удалить карту?"
        disabled={card.objectVid != null}
        onConfirm={event => deleteCard(card, event)}
        onCancel={event => event?.stopPropagation()}
      >
        <Button
          type="text"
          danger
          size="small"
          disabled={card.objectVid != null}
          icon={<DeleteOutlined />}
          onClick={event => event.stopPropagation()}
        />
      </Popconfirm>,
    }] : []),
  ];

  const filteredItems = items.filter(card =>
    hasSubstring(search, card.code)
    || hasSubstring(search, card.name)
    || hasSubstring(search, card.comment)
    || hasSubstring(search, vids.find(vid => vid.id === card.vidId)?.name));

  const tableHeight = divRef.current
    ? document.documentElement.offsetHeight - divRef.current.getBoundingClientRect().top - window.scrollY - 1
    : 0;

  return <div>
    <div className="sport-form-title">
      <span>Группа</span>
      <Select
        value={vidId}
        options={vidOptions}
        onChange={value => setVidId(value ?? null)}
        allowClear
        placeholder="Все группы"
        style={{ minWidth: 240 }}
      />
      {canEdit && <>
        <Button icon={<PlusOutlined />} onClick={() => setEditVidId(0)}>Группа</Button>
        <Button icon={<EditOutlined />} disabled={!selectedVid} onClick={() => setEditVidId(vidId)} />
        <Popconfirm
          title="Удалить группу карт?"
          description="Удаление возможно только для пустой группы."
          onConfirm={deleteVid}
        >
          <Button danger icon={<DeleteOutlined />} disabled={!selectedVid || items.length > 0} />
        </Popconfirm>
      </>}
      <Input placeholder="Поиск по коду, наименованию, группе или комментарию" value={search} onChange={e => setSearch(e.target.value)} allowClear style={{ maxWidth: 330 }} />
      {canEdit && <Button type="primary" disabled={!vidId} onClick={() => { setScanCode(""); setEditId(0); }}>Новая карта</Button>}
      {canEdit && <ScanButton title="Просканируйте код для поиска карты или добавления новой" onScan={onScan} />}
      <RefreshButton onClick={() => { getVids(); getCards(); }} />
      <Tag>{filteredItems.length} карт</Tag>
    </div>
    <div ref={divRef}>
      <Table
        columns={columns}
        dataSource={filteredItems}
        rowKey="id"
        size="small"
        pagination={false}
        loading={loading}
        scroll={{ y: tableHeight }}
        rowSelection={{
          type: "radio",
          selectedRowKeys: editId ? [editId] : [],
          renderCell: checked => <div className={checked ? "list-indicator" : ""} />,
        }}
        onRow={card => ({ onClick: () => canEdit && setEditId(card.id) })}
      />
    </div>
    {editId != null && <Card id={editId || null} vidId={vidId} vids={vids} code={scanCode} onClose={closeCard} />}
    {editVidId != null && <CardVid id={editVidId || null} onClose={closeVid} />}
    {canEdit && editId == null && editVidId == null && <Scan onScan={onScan} />}
  </div>;
};

export default Cards;
